import {create} from "zustand";
import {ReactNode} from "react";
import {GlobalModalConfiguration} from "../models/interfaces/globalModalConfiguration.interface.ts";

type ToastType = "info" | "success" | "warning" | "error";
type leftSidebarMenuState = "minimized" | "expanded" | "hidden";

interface IUiState {
  isLoading: boolean;
  leftSidebarMenuState: leftSidebarMenuState;
  toast: {
    isVisible: boolean;
    message: string;
    type: ToastType;
    position?: "top" | "bottom" | "top-right" | "top-left" | "bottom-right" | "bottom-left";
  };
  modal: {
    isVisible: boolean;
    content: ReactNode | null;
    configuration?: GlobalModalConfiguration;
  };
  drawer: {
    isVisible: boolean;
    content: ReactNode | null;
  };
  setIsLoading: (isLoading: boolean) => void;
  hideLeftSidebarMenuState: () => void;
  toggleLeftSidebarMenuState: () => void;

  showLoader: () => void;
  hideLoader: () => void;
  showToast: (message: string, type?: ToastType, position?: "top" | "bottom" | "top-right" | "top-left" | "bottom-right" | "bottom-left" ) => void;
  hideToast: () => void;
  openModal: (content: ReactNode, configuration: GlobalModalConfiguration) => void;
  closeModal: () => Promise<void>;
  updateModalConfiguration: (configuration: Partial<GlobalModalConfiguration>) => void;
  openDrawer: (content: ReactNode) => void;
  closeDrawer: () => void;
}

const useUiStore = create<IUiState>((set) => ({
  isLoading: false,
  leftSidebarMenuState: import.meta.env.VITE_LEFT_SIDEBAR_MENU_STATUS || "expanded",
  toast: {
    isVisible: false,
    message: "",
    type: "info",
  },
  modal: {
    isVisible: false,
    content: null,
  },
  drawer: {
    isVisible: false,
    content: null,
    position: "right",
  },
  setIsLoading: (isLoading) => set({isLoading}),
  hideLeftSidebarMenuState: () => set({leftSidebarMenuState: "hidden"}),
  toggleLeftSidebarMenuState: () =>
    set((state) => ({
      leftSidebarMenuState:
        state.leftSidebarMenuState === "expanded"
          ? "minimized"
          : "expanded"
    })),
  showLoader: () => set({isLoading: true}),
  hideLoader: () => set({isLoading: false}),
  showToast: (message, type = "info", position="bottom") =>
    set({toast: {isVisible: true, message, type, position}}),
  hideToast: () =>
    set((state) => ({toast: {...state.toast, isVisible: false}})),
  openModal: (content: ReactNode, configuration: GlobalModalConfiguration) =>
    set({
      modal: {
        isVisible: true,
        content,
        configuration: {
          className: "",
          classWrap: "",
          classButtonClose: "",
          classOverlay: "",
          presentingVideo: false,
          ...configuration, // Merge with default values
        },
      }
    }),
  closeModal: async () => {
    const {modal} = useUiStore.getState();

    if (modal.configuration?.beforeClose) {
      const shouldClose = await modal.configuration.beforeClose();
      if (!shouldClose) return; // Cancel close
    }

    set({
      modal: {isVisible: false, content: null, configuration: undefined}
    });
  },
  updateModalConfiguration: (configuration) => {
    set((state) => ({
      modal: {
        ...state.modal,
        configuration: {
          ...state.modal.configuration,
          ...configuration,
        },
      },
    }));
  },
  openDrawer: (content: ReactNode) =>
    set({drawer: {isVisible: true, content}}),
  closeDrawer: () =>
    set(() => ({drawer: {isVisible: false, content: null}})),
}));

export default useUiStore;